import { useQuery } from "@tanstack/react-query";

import type { SalesPerformance } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { salesPerformanceSchema } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

import type { DashboardQueryParams } from "./dashboard-query-params.ts";
import { buildDashboardSearchParams } from "./dashboard-query-params.ts";

const emptySalesPerformance: SalesPerformance = {
	rows: [],
	totalBrokerageRevenue: 0,
};

type UseSalesPerformanceParams = Pick<
	DashboardQueryParams,
	| "ltlBrokerIds"
	| "primaryBrokerIds"
	| "customerIds"
	| "startDate"
	| "endDate"
	| "carrierId"
>;

export const useSalesPerformance = (params: UseSalesPerformanceParams) => {
	const enabled = Boolean(params.startDate && params.endDate);

	const { data, isLoading, isFetching, isSuccess, error, refetch } = useQuery({
		queryKey: [
			"dashboardSalesPerformance",
			params.ltlBrokerIds ?? [],
			params.primaryBrokerIds ?? [],
			params.customerIds ?? [],
			params.startDate ?? null,
			params.endDate ?? null,
			params.carrierId ?? null,
		],
		queryFn: async ({ signal }) => {
			const searchParams = buildDashboardSearchParams(params, {
				dateOnly: true,
			});
			const url = `${apiRoutes.DASHBOARD_SALES_PERFORMANCE}?${searchParams.toString()}`;
			const result = await getAndValidateTraxAr(url, salesPerformanceSchema, {
				signal,
			});
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		data: data ?? emptySalesPerformance,
		isLoading,
		isFetching,
		isSuccess,
		error,
		refetch,
	};
};
